import { Box, Spinner, Stack, Divider } from "@chakra-ui/react";
import CDS from "design-system";
import { useEffect, useMemo } from "react";
import moment from "moment-timezone";
import { MeetingId } from "features/Resources/Meeting/models";
import useAvailabilityQuery from "features/Resources/Meeting/hooks/useAvailabilityQuery";
import TimeSlotRow from "./TimeSlotRow";
import { generateTimeSlotsWithContext } from "./helpers";

interface TimeSlotsListViewProps {
  meetingId: MeetingId;
  date: Date | undefined;
  timezone: string;
  additionalMeetingIds?: string[];
  selectedTime: Date | undefined;
  onSelectTime: (time: Date) => void;
}

export default function TimeSlotsListView({
  meetingId,
  date,
  timezone,
  additionalMeetingIds,
  selectedTime,
  onSelectTime,
}: TimeSlotsListViewProps) {
  const dateStr = date ? moment(date).format("YYYY-MM-DD") : "";

  const { data, isLoading, isError } = useAvailabilityQuery(meetingId, {
    date: dateStr,
    timezone,
    additionalMeetingIds,
  }, { enabled: !!date });

  const participants = useMemo(() => data?.participants ?? [], [data?.participants]);
  const suggestedTimes = useMemo(() => data?.suggestedTimes ?? [], [data?.suggestedTimes]);
  const durationMinutes = data?.meeting?.durationMinutes ?? 30;

  const slots = useMemo(() => {
    if (!date) return [];
    return generateTimeSlotsWithContext(participants, suggestedTimes, date, timezone, durationMinutes);
  }, [participants, suggestedTimes, date, timezone, durationMinutes]);

  const suggestedSlots = useMemo(() => slots.filter((s) => s.isSuggested), [slots]);
  const topSuggestedSlot = useMemo(
    () => suggestedSlots.length > 0
      ? suggestedSlots.reduce((a, b) => ((b.score ?? 0) > (a.score ?? 0) ? b : a))
      : null,
    [suggestedSlots],
  );
  const hasMultipleTimezones = useMemo(
    () => new Set(participants.map((p) => p.timezone).filter(Boolean)).size > 1,
    [participants],
  );

  // Pre-select the top suggested time on first load
  useEffect(() => {
    if (topSuggestedSlot && !selectedTime) {
      onSelectTime(topSuggestedSlot.time);
    }
  }, [topSuggestedSlot]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <Spinner size="md" mr={3} />
        <CDS.Text12 color="gray.500">Loading time slots...</CDS.Text12>
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box py={4}>
        <CDS.Text12 color="red.500">
          Unable to load availability. Please try again.
        </CDS.Text12>
      </Box>
    );
  }

  const tzAbbrev = moment.tz(date || new Date(), timezone).format("z");
  const selectedMs = selectedTime?.getTime();

  if (slots.length === 0) {
    return (
      <Box py={4}>
        <CDS.Text12 color="gray.500">
          No time slots available for this date.
        </CDS.Text12>
      </Box>
    );
  }

  return (
    <Box>
      <CDS.Text10 color="gray.500" mb={1}>
        {`Times in ${tzAbbrev}`}
      </CDS.Text10>
      <Box maxHeight="min(280px, calc(100vh - 500px))" overflowY="auto">
        {topSuggestedSlot && (
          <>
            <CDS.Text10 fontWeight="semibold" color="green.700" mb={1}>
              Suggested
            </CDS.Text10>
            <Stack spacing={1} mb={2}>
              <TimeSlotRow
                key={`suggested-${topSuggestedSlot.time.getTime()}`}
                slot={topSuggestedSlot}
                timezone={timezone}
                hasMultipleTimezones={hasMultipleTimezones}
                isSelected={selectedMs === topSuggestedSlot.time.getTime()}
                onSelect={onSelectTime}
              />
            </Stack>
            <Divider mb={2} />
          </>
        )}

        <CDS.Text10 fontWeight="semibold" color="gray.600" mb={1}>
          All Times
        </CDS.Text10>
        <Stack spacing={1}>
          {slots.map((slot) => {
            const isTopSuggested = topSuggestedSlot !== null && slot.time.getTime() === topSuggestedSlot.time.getTime();
            return (
              <TimeSlotRow
                key={slot.time.getTime()}
                slot={{ ...slot, isSuggested: isTopSuggested }}
                timezone={timezone}
                hasMultipleTimezones={hasMultipleTimezones}
                isSelected={selectedMs === slot.time.getTime()}
                onSelect={onSelectTime}
              />
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}
